import React, { useState, useEffect, useContext } from 'react';
import { Col, Row, OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import { IoCallOutline, IoWalletOutline } from 'react-icons/io5';
import { AiOutlineAndroid, AiOutlineMail, AiOutlineCalendar } from 'react-icons/ai';
import InformationBlock from 'components/general/InformationBlock';
import { GiSwipeCard } from 'react-icons/gi';
import { RiWalletLine } from 'react-icons/ri';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import { roundOfDigit } from 'components/common/utils';
import { BsFillPlugFill, BsCashCoin, BsStarFill, BsArrowUpCircle, BsLightningCharge } from 'react-icons/bs';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';
import { NavContext } from 'components/privateRoute/PrivateRoute';
import moment from 'moment';
import { FiActivity, FiCheckCircle } from 'react-icons/fi';
import { MdClose } from 'react-icons/md';

const CustomerProfile = () => {
  const { navigateTo } = useContext(NavContext);
  const customerProfileList = useSelector((state) => state.customerProfile.customerProfile);
  const phoneNumber = `${_.get(customerProfileList, 'country_code', ' ')}${_.get(customerProfileList, 'phone', ' ')}`;
  const isLoader = useSelector((state) => state.customerProfile.isLoading);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipContent, setTooltipContent] = useState('');
  const [showChargingSessionModal, setShowChargingSessionModal] = useState(false);
  const { t } = useTranslation();
  const rfidNumbers = _.map(customerProfileList.rfid, (item) => item.code);
  const rfidCardNumbers = _.map(customerProfileList.rfid, (data) => _.get(data, 'card_number', 'NA'));
  const customerGstins = _.map(customerProfileList.customer_gst_array, (gstin) => _.get(gstin, 'gstin', 'NA'));

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const tooltip = document.getElementsByClassName('more-button-tooltip')[0];
      if (tooltip && !tooltip.contains(event.target)) {
        setShowTooltip(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  const toggleTooltip = (content) => {
    setShowTooltip(!showTooltip);
    if (content) {
      setTooltipContent(content);
    }
  };

  const renderRfidInformationBlock = (icon, infoTitle, remainingNumbers, tooltipId, infoText) => {
    return (
      <Col xl={4} md={6} className="customerProfile-details--wrapper">
        <InformationBlock
          icon={icon}
          infoTitle={
            <>
              {infoTitle}
              {remainingNumbers.length > 0 && (
                <OverlayTrigger
                  placement="bottom"
                  overlay={
                    <Tooltip id={tooltipId} className="more-button-tooltip">
                      {remainingNumbers.join(', ')}
                    </Tooltip>
                  }
                  show={showTooltip && tooltipContent === tooltipId}
                  onHide={() => toggleTooltip('')}
                >
                  <span className="more-button" onClick={() => toggleTooltip(tooltipId)}>
                    {t('customerProfile.more')}
                  </span>
                </OverlayTrigger>
              )}
            </>
          }
          infoText={infoText}
        />
      </Col>
    );
  };

  const appType =
    _.get(customerProfileList, 'androidToken') && _.get(customerProfileList, 'iosToken')
      ? t('customerProfile.android_IOS')
      : (_.get(customerProfileList, 'androidToken') ? t('customerProfile.android') : t('customerProfile.na')) ||
        (_.get(customerProfileList, 'iosToken') ? t('customerProfile.iOS') : t('customerProfile.na'));

  const lastChargingSession = _.get(customerProfileList, 'last_successful_charging_session', null);
  const chargingSessionDateTime = lastChargingSession ? _.get(lastChargingSession, 'date_time', 'NA') : 'NA';
  return (
    <div className="customerProfile-details__main">
      <div className="customerProfile-details-inner">
        <SkeletonTheme baseColor="#1b2a38" highlightColor="#15212b" enableAnimation="true">
          <div className="customerProfile--content">
            <Row>
              <Col xl={4} md={6} className="customerProfile-details--wrapper">
                <InformationBlock
                  icon={isLoader ? <Skeleton circle="true" height={35} width={35} /> : <IoCallOutline />}
                  infoTitle={isLoader ? <Skeleton circle="true" height={35} width={100} /> : !_.isEmpty(phoneNumber) ? `+${phoneNumber}` : ''}
                  infoText={isLoader ? <Skeleton circle="true" height={35} width={100} /> : t('customerProfile.phoneNo')}
                />
              </Col>
              <Col xl={4} md={6} className="customerProfile-details--wrapper">
                <InformationBlock
                  icon={isLoader ? <Skeleton circle="true" height={35} width={35} /> : <AiOutlineMail />}
                  infoTitle={isLoader ? <Skeleton circle="true" height={35} width={100} /> : _.get(customerProfileList, 'email', 'NA')}
                  infoText={isLoader ? <Skeleton circle="true" height={35} width={100} /> : t('customerProfile.emailAddress')}
                />
              </Col>
              {!_.isEmpty(customerProfileList.customer_gst_array) &&
                renderRfidInformationBlock(<AiOutlineMail />, _.head(customerGstins), _.tail(customerGstins), 'rfid-tooltip', 'GSTIN')}
              {(!_.isEmpty(customerProfileList.customer_gst) || customerProfileList.customer_gst) &&
                _.isEmpty(customerProfileList.customer_gst_array) && (
                  <Col xl={4} md={6} className="customerProfile-details--wrapper">
                    <InformationBlock
                      icon={isLoader ? <Skeleton circle="true" height={35} width={35} /> : <AiOutlineMail />}
                      infoTitle={isLoader ? <Skeleton circle="true" height={35} width={100} /> : _.get(customerProfileList, 'customer_gst', 'NA')}
                      infoText={isLoader ? <Skeleton circle="true" height={35} width={100} /> : t('customerProfile.gstNo')}
                    />
                  </Col>
                )}
              {customerProfileList.role === 'fleet_member' ? null : (
                <Col xl={4} md={6} className="customerProfile-details--wrapper">
                  <InformationBlock
                    icon={isLoader ? <Skeleton circle="true" height={35} width={35} /> : <IoWalletOutline />}
                    infoTitle={
                      isLoader ? (
                        <Skeleton circle="true" height={35} width={100} />
                      ) : (
                        roundOfDigit(_.get(customerProfileList, 'wallet_balance', ''), 2)
                      )
                    }
                    infoText={isLoader ? <Skeleton circle="true" height={35} width={100} /> : t('customerProfile.walletBalance')}
                  />
                </Col>
              )}
              {/* CDB-1085 — pre-loaded credit: remaining/total. Only rendered while
                  the customer actually holds spendable credit; the backend sends
                  preloaded_balance_info as null once it is used up or expired. */}
              {!_.isEmpty(_.get(customerProfileList, 'preloaded_balance_info')) && (
                <Col xl={4} md={6} className="customerProfile-details--wrapper">
                  <InformationBlock
                    icon={isLoader ? <Skeleton circle="true" height={35} width={35} /> : <IoWalletOutline />}
                    infoTitle={
                      isLoader ? (
                        <Skeleton circle="true" height={35} width={100} />
                      ) : (
                        `${Number(_.get(customerProfileList, 'preloaded_balance_info.remaining_amount', 0)).toFixed(1)} / ${Number(
                          _.get(customerProfileList, 'preloaded_balance_info.amount', 0)
                        ).toFixed(1)}`
                      )
                    }
                    infoText={isLoader ? <Skeleton circle="true" height={35} width={100} /> : 'Pre-loaded Balance'}
                  />
                </Col>
              )}
              {/* One user, one VIN, one dealer, so a single name is the whole answer. */}
              {_.get(customerProfileList, 'dealer_customer.owns_dealer_credit') && (
                <Col xl={4} md={6} className="customerProfile-details--wrapper">
                  <InformationBlock
                    icon={isLoader ? <Skeleton circle="true" height={35} width={35} /> : <IoWalletOutline />}
                    infoTitle={
                      isLoader ? (
                        <Skeleton circle="true" height={35} width={100} />
                      ) : (
                        _.get(customerProfileList, 'dealer_customer.dealer_name') || '-'
                      )
                    }
                    infoText={isLoader ? <Skeleton circle="true" height={35} width={100} /> : 'Dealer'}
                  />
                </Col>
              )}
              {/* The fleet's programme, wherever it sits: the owner's car or a driver's. */}
              {_.get(customerProfileList, 'energy_credit') && (
                <>
                  <Col xl={4} md={6} className="customerProfile-details--wrapper">
                    <InformationBlock
                      icon={isLoader ? <Skeleton circle="true" height={35} width={35} /> : <IoWalletOutline />}
                      infoTitle={isLoader ? <Skeleton circle="true" height={35} width={100} /> : roundOfDigit(_.get(customerProfileList, 'energy_credit.balance', 0), 2)}
                      infoText={isLoader ? <Skeleton circle="true" height={35} width={100} /> : 'Energy Credit Balance'}
                    />
                  </Col>
                  <Col xl={4} md={6} className="customerProfile-details--wrapper">
                    <InformationBlock
                      icon={isLoader ? <Skeleton circle="true" height={35} width={35} /> : <IoWalletOutline />}
                      infoTitle={
                        isLoader ? (
                          <Skeleton circle="true" height={35} width={100} />
                        ) : (
                          `${roundOfDigit(_.get(customerProfileList, 'energy_credit.used', 0), 2)} / ${roundOfDigit(_.get(customerProfileList, 'energy_credit.total', 0), 2)}`
                        )
                      }
                      infoText={isLoader ? <Skeleton circle="true" height={35} width={100} /> : 'Energy Credit Used'}
                    />
                  </Col>
                  <Col xl={4} md={6} className="customerProfile-details--wrapper">
                    <InformationBlock
                      icon={isLoader ? <Skeleton circle="true" height={35} width={35} /> : <GiSwipeCard />}
                      infoTitle={isLoader ? <Skeleton circle="true" height={35} width={100} /> : _.get(customerProfileList, 'energy_credit.verified_evcc_id') || '-'}
                      infoText={isLoader ? <Skeleton circle="true" height={35} width={100} /> : 'Programme MAC ID'}
                    />
                  </Col>
                  <Col xl={4} md={6} className="customerProfile-details--wrapper">
                    <InformationBlock
                      icon={isLoader ? <Skeleton circle="true" height={35} width={35} /> : <BsFillPlugFill />}
                      infoTitle={
                        isLoader ? (
                          <Skeleton circle="true" height={35} width={100} />
                        ) : (
                          `${_.get(customerProfileList, 'energy_credit.vehicle.rto_no', '-')}${
                            _.get(customerProfileList, 'energy_credit.held_by.name') ? ` (${_.get(customerProfileList, 'energy_credit.held_by.name')})` : ''
                          }`
                        )
                      }
                      infoText={isLoader ? <Skeleton circle="true" height={35} width={100} /> : 'Programme Vehicle'}
                    />
                  </Col>
                </>
              )}
              <Col xl={4} md={6} className="customerProfile-details--wrapper">
                <InformationBlock
                  icon={isLoader ? <Skeleton circle="true" height={35} width={35} /> : <AiOutlineAndroid />}
                  infoTitle={isLoader ? <Skeleton circle="true" height={35} width={100} /> : appType}
                  infoText={isLoader ? <Skeleton circle="true" height={35} width={100} /> : t('customerProfile.appType')}
                />
              </Col>
              {!_.isEmpty(customerProfileList.rfid) &&
                renderRfidInformationBlock(<GiSwipeCard />, _.head(rfidNumbers), _.tail(rfidNumbers), 'rfid-tooltip', 'RFID Number')}
              {!_.isEmpty(customerProfileList.rfid) &&
                renderRfidInformationBlock(
                  <GiSwipeCard />,
                  _.head(rfidCardNumbers),
                  _.tail(rfidCardNumbers),
                  'rfid-card-tooltip',
                  'RFID Card Number'
                )}
              <Col xl={4} md={6} className="customerProfile-details--wrapper">
                <InformationBlock
                  icon={isLoader ? <Skeleton circle="true" height={35} width={35} /> : <BsFillPlugFill />}
                  infoTitle={isLoader ? <Skeleton circle="true" height={35} width={100} /> : _.get(customerProfileList, 'total_session', 'NA')}
                  infoText={isLoader ? <Skeleton circle="true" height={35} width={100} /> : t('customerProfile.totalSessions')}
                />
              </Col>
              {customerProfileList.role === 'fleet_member' ? null : (
                <Col xl={4} md={6} className="customerProfile-details--wrapper">
                  <InformationBlock
                    icon={isLoader ? <Skeleton circle="true" height={35} width={35} /> : <RiWalletLine />}
                    infoTitle={isLoader ? <Skeleton circle="true" height={35} width={100} /> : _.get(customerProfileList, 'total_spent', 'NA')}
                    infoText={isLoader ? <Skeleton circle="true" height={35} width={100} /> : t('customerProfile.totalSpent')}
                  />
                </Col>
              )}
              <Col xl={4} md={6} className="customerProfile-details--wrapper">
                <InformationBlock
                  icon={isLoader ? <Skeleton circle="true" height={35} width={35} /> : <BsLightningCharge />}
                  infoTitle={
                    isLoader ? (
                      <Skeleton circle="true" height={35} width={100} />
                    ) : (
                      _.get(customerProfileList, 'total_units_consumed')
                        ? parseFloat(_.get(customerProfileList, 'total_units_consumed', 0)).toFixed(2)
                        : '0.00'
                    )
                  }
                  infoText={isLoader ? <Skeleton circle="true" height={35} width={100} /> : t('customerProfile.total_units_consumed')}
                />
              </Col>
              
              {customerProfileList.role === 'fleet_member' ? null : (
                <Col xl={4} md={6} className="customerProfile-details--wrapper">
                  <InformationBlock
                    icon={isLoader ? <Skeleton circle="true" height={35} width={35} /> : <BsArrowUpCircle />}
                    infoTitle={
                      isLoader ? (
                        <Skeleton circle="true" height={35} width={100} />
                      ) : (
                        _.get(customerProfileList, 'total_wallet_topup', '') !== '' && _.get(customerProfileList, 'total_wallet_topup', '') !== null
                          ? parseFloat(_.get(customerProfileList, 'total_wallet_topup', 0)).toFixed(1)
                          : 'NA'
                      )
                    }
                    infoText={isLoader ? <Skeleton circle="true" height={35} width={100} /> : t('customerProfile.totalWalletTopup')}
                  />
                </Col>
              )}
              {customerProfileList.role === 'fleet_member' ? null : (
                <Col xl={4} md={6} className="customerProfile-details--wrapper">
                  <InformationBlock
                    icon={isLoader ? <Skeleton circle="true" height={35} width={35} /> : <BsCashCoin />}
                    infoTitle={isLoader ? <Skeleton circle="true" height={35} width={100} /> : _.get(customerProfileList, 'chargecoins_v2', 'NA')}
                    infoText={isLoader ? <Skeleton circle="true" height={35} width={100} /> : t('customerProfile.chargeCoins')}
                  />
                </Col>
              )}
              {customerProfileList.role === 'fleet_member' ? null : (
                <Col xl={4} md={6} className="customerProfile-details--wrapper">
                  <InformationBlock
                    icon={isLoader ? <Skeleton circle="true" height={35} width={35} /> : <FiActivity />}
                    infoTitle={isLoader ? <Skeleton circle="true" height={35} width={100} /> : _.get(customerProfileList, 'currentLevel.name', 'NA')}
                    infoText={isLoader ? <Skeleton circle="true" height={35} width={100} /> : t('customerProfile.currentLevel')}
                  />
                </Col>
              )}
              {customerProfileList.role === 'fleet_member' ? null : (
                <Col xl={4} md={6} className="customerProfile-details--wrapper">
                  <InformationBlock
                    icon={isLoader ? <Skeleton circle="true" height={35} width={35} /> : <BsStarFill />}
                    infoTitle={
                      isLoader ? (
                        <Skeleton circle="true" height={35} width={100} />
                      ) : (
                        _.get(customerProfileList, 'average_rating') != null
                          ? `${parseFloat(_.get(customerProfileList, 'average_rating')).toFixed(1)} / 5`
                          : 'NA'
                      )
                    }
                    infoText={isLoader ? <Skeleton circle="true" height={35} width={100} /> : t('customerProfile.averageRating')}
                  />
                </Col>
              )}
              {customerProfileList.role === 'fleet_member' ? null : (
                <Col xl={4} md={6} className="customerProfile-details--wrapper">
                  <InformationBlock
                    icon={isLoader ? <Skeleton circle="true" height={35} width={35} /> : <AiOutlineCalendar />}
                    infoTitle={
                      isLoader ? (
                        <Skeleton circle="true" height={35} width={100} />
                      ) : (
                        _.get(customerProfileList, 'userCreatedOn')
                          ? `${moment(_.get(customerProfileList, 'userCreatedOn'), 'DD/MM/YYYY HH:mm:ss').format('DD/MM/YYYY HH:mm:ss')}`
                          : 'NA'
                      )
                    }
                    infoText={isLoader ? <Skeleton circle="true" height={35} width={100} /> : t('customerProfile.accountCreationDate')}
                  />
                </Col>
              )}
              {customerProfileList.role === 'fleet_member' && (
                <Col xl={4} md={6} className="customerProfile-details--wrapper">
                  <InformationBlock
                    icon={isLoader ? <Skeleton circle={true} height={35} width={35} /> : <FiActivity />}
                    infoTitle={
                      isLoader ? (
                        <Skeleton height={35} width={100} />
                      ) : (
                        <span
                          style={{ cursor: 'pointer' }}
                          onClick={() => navigateTo(`/customerProfile/${_.get(customerProfileList, 'fleet_owner.id', 'NA')}`)}
                        >
                          {_.get(customerProfileList, 'fleet_owner.name', 'NA')}
                          <div>
                            +{_.get(customerProfileList, 'fleet_owner.country_code', '')}
                            {_.get(customerProfileList, 'fleet_owner.phone', 'NA')}
                          </div>
                        </span>
                      )
                    }
                    infoText={isLoader ? <Skeleton height={35} width={100} /> : t('customerProfile.fleetMember')}
                  />
                </Col>
              )}
              {lastChargingSession && (
                <Col xl={4} md={6} className="customerProfile-details--wrapper">
                  <InformationBlock
                    icon={
                      isLoader ? (
                        <Skeleton circle="true" height={35} width={35} />
                      ) : (
                        <span
                          style={{ cursor: 'pointer' }}
                          onClick={() => setShowChargingSessionModal(true)}
                        >
                          <FiCheckCircle />
                        </span>
                      )
                    }
                    infoTitle={
                      isLoader ? (
                        <Skeleton circle="true" height={35} width={100} />
                      ) : (
                        <span
                          style={{ cursor: 'pointer' }}
                          onClick={() => setShowChargingSessionModal(true)}
                        >
                          {chargingSessionDateTime}
                        </span>
                      )
                    }
                    infoText={isLoader ? <Skeleton circle="true" height={35} width={100} /> : t('customerProfile.lastSuccessfulChargingSession')}
                  />
                </Col>
              )}
            </Row>
          </div>
        </SkeletonTheme>
      </div>

      <Modal show={showChargingSessionModal} onHide={() => setShowChargingSessionModal(false)} centered>
        <Modal.Header style={{ color: '#fff' }}>
          <div className="d-flex justify-content-between align-items-center w-100">
            <Modal.Title style={{ color: '#fff', fontSize: '18px' }}>{t('customerProfile.lastSuccessfulChargingSession')}</Modal.Title>
            <button onClick={() => setShowChargingSessionModal(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#fff' }}>
              <MdClose />
            </button>
          </div>
        </Modal.Header>
        <Modal.Body style={{ color: '#fff' }}>
          {(() => {
            const sessionData = _.get(customerProfileList, 'last_successful_charging_session', null);
            return sessionData ? (
              <div className="charging-session-details" style={{ color: '#fff' }}>
                <div className="mb-3">
                  <small className="d-block" style={{ color: '#ccc' }}>{t('customerProfile.chargerId')}</small>
                  <span className="fw-medium" style={{ color: '#fff' }}>{_.get(sessionData, 'charger_id', 'NA')}</span>
                </div>
                <div className="mb-3">
                  <small className="d-block" style={{ color: '#ccc' }}>{t('customerProfile.unitsConsumed')}</small>
                  <span className="fw-medium" style={{ color: '#fff' }}>{_.get(sessionData, 'units_consumed', 'NA')}</span>
                </div>
                <div className="mb-3">
                  <small className="d-block" style={{ color: '#ccc' }}>{t('customerProfile.totalAmount')}</small>
                  <span className="fw-medium" style={{ color: '#fff' }}>
                    {_.get(sessionData, 'total_amount') !== undefined && _.get(sessionData, 'total_amount') !== null
                      ? roundOfDigit(_.get(sessionData, 'total_amount'), 2)
                      : 'NA'}
                  </span>
                </div>
              </div>
            ) : (
              <div className="text-center py-3" style={{ color: '#fff' }}>
                <span>{t('customerProfile.na')}</span>
              </div>
            );
          })()}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default CustomerProfile;